import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { ChevronDown, Columns, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ActionButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MenuRearrangeList } from '@/routes/restaurant/menu/MenuRearrangeList';
import { useIsMenuEditable } from '@/routes/restaurant/menu/useIsMenuEditable';
import { useMenu } from '@/routes/restaurant/useRestaurantData';
import { MenuItemsTable } from './MenuItemsTable';
import type { ColumnVisibleType } from './menuItemColumns';

export const Route = createFileRoute('/r/$restaurantId/menu/menu-items')({
  component: MenuItemListPage,
});

function MenuItemListPage() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const menu = useMenu();
  const isMenuEditable = useIsMenuEditable();
  const [columnsVisible, setColumnsVisible] = useState<ColumnVisibleType>('standard');
  const [categoryUuidFilter, setCategoryUuidFilter] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // The category tab clicked while there were unsaved changes, waiting on the discard prompt.
  const [pendingCategoryUuid, setPendingCategoryUuid] = useState<string | null>(null);
  const [isRearranging, setIsRearranging] = useState(false);
  const [isCreatingMenu, setIsCreatingMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const clearSearch = () => setSearchTerm('');

  // Reset `isRearranging` and clear search when switching categories
  useEffect(() => {
    setIsRearranging(false);
    clearSearch();
  }, [categoryUuidFilter]);

  // Focus on the search input when "All" category tab is selected
  useLayoutEffect(() => {
    if (categoryUuidFilter === '') {
      searchInputRef.current?.focus();
    }
  }, [categoryUuidFilter]);

  // If no menu is setup yet
  if (menu == null) {
    // A menu from an integration arrives with its first sync.
    if (!isMenuEditable) return null;
    const handleSetupMenu = async () => {
      try {
        setIsCreatingMenu(true);
        await convex.mutation(api.menus.initMenu, { restaurantId });
        toast.success('New menu created successfully');
      } catch (error) {
        const description = getUserErrorMessage(error);
        toast.error('Unable to create new menu', { description });
      }
      // Not in a `finally`: React Compiler can't compile one and skips this whole component, so
      // `filteredMenuItems` becomes a new array every render and resets the table's unsaved rows.
      setIsCreatingMenu(false);
    };
    return (
      <ActionButton loading={isCreatingMenu} onClick={handleSetupMenu}>
        Setup menu
      </ActionButton>
    );
  }

  const { categories, menuItems, modifierGroups, modifiers } = menu;

  if (categories.length === 0) {
    return (
      <div className={'bg-white p-4 text-center border'}>
        <div>Add some categories first.</div>
      </div>
    );
  }

  // Filter menu items based on search term and category
  const filteredMenuItems = menuItems.filter((item) => {
    const matchesCategory = categoryUuidFilter ? item.categoryUuid === categoryUuidFilter : true;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div>
      <div className="mb-2">
        {/* Category (filter) tabs */}
        <Tabs
          className="w-full"
          onValueChange={(value) =>
            hasUnsavedChanges ? setPendingCategoryUuid(value) : setCategoryUuidFilter(value)
          }
          value={categoryUuidFilter}
        >
          <TabsList className="inline-flex flex-wrap bg-white dark:bg-muted h-auto">
            <TabsTrigger
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              value=""
            >
              All
            </TabsTrigger>
            {categories.map((c) => (
              <TabsTrigger
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground hover:bg-primary/10"
                key={c.uuid}
                value={c.uuid}
              >
                {c.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Switching categories remounts the table, which throws away its unsaved changes */}
      <AlertDialog
        onOpenChange={(open) => !open && setPendingCategoryUuid(null)}
        open={pendingCategoryUuid !== null}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard your changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Switching categories will lose them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingCategoryUuid !== null) setCategoryUuidFilter(pendingCategoryUuid);
              }}
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!isRearranging && (
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="relative max-w-xs">
            <Input
              className="pr-8"
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search menu items..."
              ref={searchInputRef}
              value={searchTerm}
            />
            {searchTerm && (
              <ActionButton
                className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 p-0"
                onClick={clearSearch}
                size="icon"
                variant="ghost"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </ActionButton>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ActionButton className="flex" size="sm" variant="outline">
                <Columns className="h-4 w-4" />
                Columns
                <ChevronDown className="h-4 w-4" />
              </ActionButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={'end'} className="w-56">
              <DropdownMenuRadioGroup
                onValueChange={(value) =>
                  setColumnsVisible(value === 'extra' ? 'extra' : 'standard')
                }
                value={columnsVisible}
              >
                <DropdownMenuRadioItem value="standard">Prices, mods</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="extra">Visibility, desc, GST</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* `key` is set to `categoryIdFilter` to force a re-render when categoryIdFilter value changes */}
      <div key={categoryUuidFilter}>
        {isRearranging ? (
          <MenuRearrangeList
            items={menuItems.filter((mi) => mi.categoryUuid === categoryUuidFilter)}
            itemsLabel="items"
            onRearrangingDone={() => {
              setIsRearranging(false);
              clearSearch();
            }}
            onSave={(sortedMenuItemUuids) =>
              convex.mutation(api.menus.updateMenuItemsSortOrder, {
                menuId: menu._id,
                sortedMenuItemUuids,
              })
            }
          />
        ) : (
          <MenuItemsTable
            categories={categories}
            // Replace '' with undefined
            categoryUuidFilter={categoryUuidFilter === '' ? undefined : categoryUuidFilter}
            columnsVisible={columnsVisible}
            menuId={menu._id}
            menuItems={filteredMenuItems}
            modifierGroups={modifierGroups}
            modifiers={modifiers}
            onHasChangesChange={setHasUnsavedChanges}
            onRearrange={() => {
              setIsRearranging(true);
              clearSearch();
            }}
          />
        )}
      </div>
    </div>
  );
}
